import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

function QuarterComparisonView({ user, selectedYear, months }) {
  const [quarterData, setQuarterData] = useState({ income: [], expense: [], savings: [] });
  const [loading, setLoading] = useState(true);

  const quarters = [
    { name: 'Q1', months: [0, 1, 2], label: 'Jan-Mar' },
    { name: 'Q2', months: [3, 4, 5], label: 'Apr-Jun' },
    { name: 'Q3', months: [6, 7, 8], label: 'Jul-Sep' },
    { name: 'Q4', months: [9, 10, 11], label: 'Oct-Dec' }
  ];

  useEffect(() => {
    loadQuarterData();
  }, [selectedYear]);

  // Format number with thousand separators and brackets for negatives
  const formatNumber = (num, decimals = 2) => {
    const isNegative = num < 0;
    const absNum = Math.abs(num);
    const formatted = absNum.toLocaleString('en-GB', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return isNegative ? `(${formatted})` : formatted;
  };

  const loadQuarterData = async () => {
    setLoading(true);
    
    try {
      // Load categories
      const incomeSnapshot = await getDocs(collection(db, 'users', user.uid, 'income_categories'));
      const expenseSnapshot = await getDocs(collection(db, 'users', user.uid, 'expense_categories'));
      
      const incomeCategories = incomeSnapshot.docs.map(doc => doc.data().name?.trim()).filter(n => n).sort();
      const expenseCategories = expenseSnapshot.docs.map(doc => doc.data().name?.trim()).filter(n => n).sort();
      
      // Load savings accounts
      const q = query(
        collection(db, 'users', user.uid, 'account_setup'),
        where('active', '==', true)
      );
      const savingsSnapshot = await getDocs(q);
      const savingsAccounts = [];
      
      savingsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const accountName = data.name || '';
        const accountGroup = data.group || '';
        const isSavings = accountGroup.toLowerCase().includes('saving');
        
        if (isSavings && accountName) {
          savingsAccounts.push(accountName);
        }
      });
      
      const savingsCategories = savingsAccounts.sort();
      
      // Load all 12 months data
      const monthlyDataPromises = [];
      for (let m = 0; m < 12; m++) {
        const monthStr = String(m + 1).padStart(2, '0');
        const budgetDocId = `budget_${selectedYear}_${monthStr}`;
        monthlyDataPromises.push(getDoc(doc(db, 'users', user.uid, 'Budgets', budgetDocId)));
      }
      
      const monthlySnapshots = await Promise.all(monthlyDataPromises);
      const monthlyData = monthlySnapshots.map(snap => snap.exists() ? snap.data() : {});
      
      // Build income quarter data - DON'T use Math.abs
      const incomeData = incomeCategories.map(category => {
        const quarterTotals = quarters.map(quarter => {
          return quarter.months.reduce((sum, monthIndex) => {
            const actual = monthlyData[monthIndex]?.income?.[category]?.actual || 0;
            return sum + actual;
          }, 0);
        });
        
        return {
          category,
          q1: quarterTotals[0],
          q2: quarterTotals[1],
          q3: quarterTotals[2],
          q4: quarterTotals[3],
          total: quarterTotals.reduce((sum, val) => sum + val, 0)
        };
      });
      
      // Build expense quarter data - DON'T use Math.abs
      const expenseData = expenseCategories.map(category => {
        const quarterTotals = quarters.map(quarter => {
          return quarter.months.reduce((sum, monthIndex) => {
            const actual = monthlyData[monthIndex]?.expense?.[category]?.actual || 0;
            return sum + actual;
          }, 0);
        });
        
        return {
          category,
          q1: quarterTotals[0],
          q2: quarterTotals[1],
          q3: quarterTotals[2],
          q4: quarterTotals[3],
          total: quarterTotals.reduce((sum, val) => sum + val, 0)
        };
      });
      
      // Build savings quarter data
      const savingsData = savingsCategories.map(category => {
        const quarterTotals = quarters.map(quarter => {
          return quarter.months.reduce((sum, monthIndex) => {
            const actual = monthlyData[monthIndex]?.savings?.[category]?.actual || 0;
            return sum + actual;
          }, 0);
        });
        
        return {
          category,
          q1: quarterTotals[0],
          q2: quarterTotals[1],
          q3: quarterTotals[2],
          q4: quarterTotals[3],
          total: quarterTotals.reduce((sum, val) => sum + val, 0)
        };
      });
      
      setQuarterData({
        income: incomeData,
        expense: expenseData,
        savings: savingsData
      });
      
    } catch (error) {
      console.error('Error loading quarter data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderQuarterTable = (data, title, colorClass) => {
    if (data.length === 0) return null;
    
    const q1Total = data.reduce((sum, item) => sum + item.q1, 0);
    const q2Total = data.reduce((sum, item) => sum + item.q2, 0);
    const q3Total = data.reduce((sum, item) => sum + item.q3, 0);
    const q4Total = data.reduce((sum, item) => sum + item.q4, 0);
    const grandTotal = data.reduce((sum, item) => sum + item.total, 0);
    
    return (
      <div className="quarter-section">
        <h3 className={`section-title ${colorClass}`}>{title}</h3>
        <div className="rolling-table-wrapper">
          <table className="rolling-table">
            <thead>
              <tr>
                <th className="sticky-col">CATEGORY</th>
                <th className="right">Q1 (Jan-Mar)</th>
                <th className="right">Q2 (Apr-Jun)</th>
                <th className="right">Q3 (Jul-Sep)</th>
                <th className="right">Q4 (Oct-Dec)</th>
                <th className="right">TOTAL YEAR</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={index}>
                  <td className="sticky-col"><strong>{item.category}</strong></td>
                  <td className="right">{formatNumber(item.q1)}</td>
                  <td className="right">{formatNumber(item.q2)}</td>
                  <td className="right">{formatNumber(item.q3)}</td>
                  <td className="right">{formatNumber(item.q4)}</td>
                  <td className="right"><strong>{formatNumber(item.total)}</strong></td>
                </tr>
              ))}
              <tr className="summary-row">
                <td className="sticky-col"><strong>TOTAL {title.toUpperCase()}</strong></td>
                <td className="right"><strong>{formatNumber(q1Total)}</strong></td>
                <td className="right"><strong>{formatNumber(q2Total)}</strong></td>
                <td className="right"><strong>{formatNumber(q3Total)}</strong></td>
                <td className="right"><strong>{formatNumber(q4Total)}</strong></td>
                <td className="right"><strong>{formatNumber(grandTotal)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading-state">Loading quarterly data...</div>;
  }

  return (
    <div className="quarter-view">
      <h3>Quarterly Comparison for {selectedYear}</h3>
      {renderQuarterTable(quarterData.income, 'Income', 'income')}
      {renderQuarterTable(quarterData.savings, 'Savings', 'savings')}
      {renderQuarterTable(quarterData.expense, 'Expenses', 'expense')}
    </div>
  );
}

export default QuarterComparisonView;